import React, { useState } from 'react';
import { Box, Collapse, Typography } from '@mui/material';
import { LightbulbOutlined } from '@mui/icons-material';
import { useGlobalization } from '@Translations/useGlobalization';
import { TFunc } from '@Translations/types';

interface SectionCopy {
	title: string;
	description: string;
	toggleLabel: string;
	helpId: string;
	introLine: string;
	categoriesExplanation: string;
	componentsExplanation: string;
	scoreTableExplanation: string;
	asymmetryExplanation: string;
}

const getSectionCopy = (section: 'lof' | 'cof', t: TFunc): SectionCopy => {
	if (section === 'lof') {
		return {
			title: t('Likelihood of Failure (LoF) Setup'),
			description: t(
				'Likelihood of Failure (LoF) captures how likely a pipe is to fail, based on factors like condition, material, age or repair history.',
			),
			toggleLabel: t('How LoF calculation works'),
			helpId: 'lof-calculation-help',
			introLine: t('LoF is organised in two levels:'),
			categoriesExplanation: t(
				'Categories — broad groupings (e.g. "Condition", "Asset Attributes"). Each category has a weight, and all category weights must add up to 100%.',
			),
			componentsExplanation: t(
				'Components — the individual factors inside a category (e.g. "CCTV Grade", "Pipe Material"). Each has a weight within its category, and all component weights within a category must also add up to 100%.',
			),
			scoreTableExplanation: t(
				'Each component has a score table — you assign a score to each value in your data, including "—" for missing values. Industry practice typically uses 2–4 components across 1–3 categories.',
			),
			asymmetryExplanation: t(
				'LoF always includes a built-in Condition category for inspection results — you can add more categories alongside it. (CoF, by contrast, starts empty — you build it from scratch.)',
			),
		};
	}
	return {
		title: t('Consequence of Failure (CoF) Setup'),
		description: t(
			'Consequence of Failure (CoF) captures the impact of a pipe failing, based on factors like proximity to critical infrastructure, customer count or road classification.',
		),
		toggleLabel: t('How CoF calculation works'),
		helpId: 'cof-calculation-help',
		introLine: t('CoF is organised in two levels:'),
		categoriesExplanation: t(
			'Categories — broad groupings (e.g. "Social", "Environmental"). Each category has a weight, and all category weights must add up to 100%.',
		),
		componentsExplanation: t(
			'Components — the individual factors inside a category (e.g. "Proximity to Schools", "Road Classification"). Each has a weight within its category, and all component weights within a category must also add up to 100%.',
		),
		scoreTableExplanation: t(
			'Each component has a score table — you assign a score to each value in your data, including "—" for missing values. Industry practice typically uses 2–4 components across 1–3 categories.',
		),
		asymmetryExplanation: t(
			'CoF starts empty — you build it from scratch by adding categories and components. (LoF, by contrast, always includes a built-in Condition category for inspection results.)',
		),
	};
};

interface SetupIntroProps {
	section: 'lof' | 'cof';
}

// The page title, description, and "How LoF/CoF calculation works"
// disclosure — shared between LoFSetupIntro and CoFSetupIntro, which are
// otherwise identical apart from copy. Self-contained (owns its own
// open/closed state) so this doesn't add to LoFSetup/CoFSetup's own
// complexity.
export const SetupIntro = ({ section }: SetupIntroProps) => {
	const { t } = useGlobalization();
	const [helpOpen, setHelpOpen] = useState(false);
	const copy = getSectionCopy(section, t);

	return (
		<Box sx={{ mb: 3 }}>
			<Typography
				sx={{
					fontSize: 20,
					fontWeight: 700,
					color: '#0d1520',
					mb: 0.5,
				}}>
				{copy.title}
			</Typography>
			<Typography sx={{ fontSize: 13, color: '#5b6472' }}>
				{copy.description}
			</Typography>
			<Box
				component="button"
				onClick={() => setHelpOpen(o => !o)}
				aria-expanded={helpOpen}
				aria-controls={copy.helpId}
				sx={{
					display: 'inline-flex',
					alignItems: 'center',
					gap: 1,
					border: 'none',
					background: 'transparent',
					cursor: 'pointer',
					font: 'inherit',
					fontSize: 13,
					fontWeight: 700,
					color: '#1f2733',
					p: 0,
				}}>
				<Box
					component="span"
					sx={{
						fontSize: 11,
						color: '#97a0ac',
						transition: 'transform .2s ease',
						transform: helpOpen ? 'rotate(90deg)' : 'none',
					}}>
					▸
				</Box>
				<LightbulbOutlined sx={{ fontSize: 16, color: '#97a0ac' }} />
				{copy.toggleLabel}
			</Box>
			<Collapse in={helpOpen}>
				<Box
					id={copy.helpId}
					sx={{
						mt: 1.25,
						fontSize: 13,
						color: '#3a4451',
						maxWidth: 900,
					}}>
					<Typography sx={{ fontSize: 13, mb: 0.5 }}>
						{copy.introLine}
					</Typography>
					<Box component="ul" sx={{ pl: 2.5, m: 0, mb: 0.75 }}>
						<Typography
							component="li"
							sx={{ fontSize: 13, mb: 0.5 }}>
							{copy.categoriesExplanation}
						</Typography>
						<Typography component="li" sx={{ fontSize: 13 }}>
							{copy.componentsExplanation}
						</Typography>
					</Box>
					<Typography sx={{ fontSize: 13 }}>
						{copy.scoreTableExplanation}
					</Typography>
					<Typography sx={{ fontSize: 13, mt: 0.75 }}>
						{copy.asymmetryExplanation}
					</Typography>
				</Box>
			</Collapse>
		</Box>
	);
};
